<template>
    <div class="rail-panel">
        <div class="rail-panel__head">
            <span class="rail-panel__title stock-title">
                <v-icon x-small class="stock-title__icon">fas fa-boxes</v-icon>
                Existencias
            </span>
            <span class="rail-panel__subject">{{ productName }}</span>
        </div>

        <!-- Lo que queda después de esta venta: la barra es la existencia que
             sigue disponible y se vuelve naranja cuando queda poco. -->
        <div class="stock-summary">
            <div class="stock-summary__top">
                <span class="stock-summary__main">{{ totalPieces }} pza</span>
                <span class="stock-summary__kg" v-if="totalKg > 0">{{ totalKg }} kg</span>
            </div>
            <div class="stock-summary__meter" role="img"
            :aria-label="`Quedan ${remainingPieces} de ${totalPieces} piezas`">
                <span class="stock-summary__fill" :class="{ 'stock-summary__fill--low': isLow }"
                :style="{ width: remainingPct + '%' }"></span>
            </div>
            <div class="stock-summary__caption">
                <span v-if="inCartPieces > 0">En la venta {{ inCartPieces }} pza · </span>quedan {{ remainingPieces }} pza<span v-if="totalKg > 0"> · {{ remainingKg }} kg</span>
            </div>
        </div>

        <table class="rail-table">
            <thead>
                <tr>
                    <th>Folio</th>
                    <th>Existencia</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="entry in productInStock" :key="entry.id">
                    <td class="text-truncate">{{ entry.ticketFolio }}</td>
                    <td>{{ entry.existencia }}</td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
import { toNumber, roundMoney } from '@/utils/CurrencyUtil';

export default {
    name: 'DetailProductInStock',
    props: {
        productInStock: { type: Array, default: () => [] },
        // El carrito de la venta: sirve para calcular cuánto queda de este
        // producto. Es solo lectura; el panel nunca lo modifica.
        cart: { type: Array, default: () => [] }
    },
    computed: {
        productName() {
            // El nombre del producto viene anidado y puede faltar mientras llega la respuesta.
            return this.productInStock[0]?.producto?.nombre || '';
        },
        productId() {
            return this.productInStock[0]?.productoId ?? this.productInStock[0]?.producto?.id ?? null;
        },
        totalPieces() {
            return roundMoney(this.productInStock.reduce((acc, e) => acc + toNumber(e.existencia, 0), 0));
        },
        // El peso por pieza viene en cada entrada (`averageWeight`); si no
        // viene, el kilaje no se muestra en vez de inventarse.
        totalKg() {
            return roundMoney(this.productInStock.reduce(
                (acc, e) => acc + toNumber(e.existencia, 0) * toNumber(e.averageWeight, 0), 0));
        },
        inCartPieces() {
            if (this.productId == null) return 0;
            return roundMoney(this.cart
                .filter(item => item.productoId == this.productId)
                .reduce((acc, item) => acc + toNumber(item.cantidad, 0), 0));
        },
        remainingPieces() {
            return roundMoney(Math.max(0, this.totalPieces - this.inCartPieces));
        },
        remainingKg() {
            if (this.totalPieces <= 0) return 0;
            return roundMoney(this.totalKg * (this.remainingPieces / this.totalPieces));
        },
        remainingPct() {
            if (this.totalPieces <= 0) return 0;
            return Math.round((this.remainingPieces / this.totalPieces) * 100);
        },
        isLow() {
            return this.remainingPct <= 20;
        }
    }
}
</script>

<style scoped>
    .stock-title {
        display: inline-flex;
        align-items: center;
        gap: 6px;
    }
    .stock-title__icon {
        color: var(--v-primary-base) !important;
    }
    /* Resumen como ficha tintada: lo primero que se lee del panel. */
    .stock-summary {
        margin: 10px 0 6px;
        padding: 10px 12px;
        border-radius: 6px;
        background: color-mix(in srgb, var(--app-primary) 6%, transparent);
    }
    /* Filas del detalle con aire y filete, la existencia en pastilla. */
    .rail-table ::v-deep td {
        padding: 8px 0;
        border-bottom: 1px solid var(--app-line);
    }
    .rail-table ::v-deep tbody tr:last-child td { border-bottom: 0; }
    .rail-table ::v-deep td:last-child {
        font-variant-numeric: tabular-nums;
    }
    .stock-summary__top {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        font-variant-numeric: tabular-nums;
    }
    .stock-summary__main {
        font-size: 14px;
        font-weight: 700;
        color: var(--app-text);
    }
    .stock-summary__kg {
        font-size: 13px;
        font-weight: 500;
        color: var(--app-text-muted);
    }
    .stock-summary__meter {
        height: 6px;
        margin: 6px 0 4px;
        border-radius: 3px;
        background: var(--app-line);
        overflow: hidden;
    }
    .stock-summary__fill {
        display: block;
        height: 100%;
        background: var(--app-primary);
        transition: width .2s ease;
    }
    .stock-summary__fill--low {
        background: #E65100;
    }
    .stock-summary__caption {
        font-size: 12px;
        color: var(--app-text-muted);
        font-variant-numeric: tabular-nums;
    }
</style>
